import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, PartyPopper } from "lucide-react";import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const HELP_OPTIONS = [
  "Immigration", "Documents From Another Country", "Passport / ID", "Notary / Apostille",
  "ITIN / Tax", "Drug Testing / Background Check", "Starting Life in the U.S.", "Citizenship", "Not Sure",
];

const URGENT_QUESTIONS = [
  { key: "detained", label: "Are you currently detained?" },
  { key: "court_date", label: "Do you have an immigration court date?" },
  { key: "rfe", label: "Have you received a Request for Evidence (RFE)?" },
  { key: "noid", label: "Have you received a Notice of Intent to Deny?" },
  { key: "denial", label: "Have you received a denial?" },
  { key: "interview", label: "Do you have an interview scheduled?" },
  { key: "passport_expiring", label: "Is your passport expiring soon?" },
  { key: "urgent_travel", label: "Do you have urgent international travel?" },
];

const APPOINTMENT_TYPES = [
  "Immigration Consultation", "Document Retrieval Consultation", "ITIN Appointment",
  "Notary / Apostille Appointment", "Drug Testing Appointment", "New Life Navigation Session",
];

const STEP_LABELS = ["What you need", "Urgent matters", "About you", "Details", "Documents", "Appointment", "Review", "Done"];

export default function StartJourney() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [helpNeeded, setHelpNeeded] = useState([]);
  const [urgent, setUrgent] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", phone: "", language: "English", city: "", country_of_birth: "", consent: false });
  const [details, setDetails] = useState({ situation: "", documents_ready: "" });
  const [appt, setAppt] = useState({ service_type: "", mode: "", date: "", time: "" });

  const toggleHelp = (opt) => {
    setHelpNeeded((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
  };

  const hasUrgent = Object.values(urgent).some(Boolean);

  const canNext = () => {
    if (step === 0) return helpNeeded.length > 0;
    if (step === 2) return contact.name && contact.email && contact.phone && contact.consent;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/intakes", {
        help_needed: helpNeeded,
        urgent,
        contact,
        details,
        appointment_pref: appt,
      });
      const intakeId = res.data.intake_id;
      try { localStorage.setItem("pending_intake", intakeId); } catch (e) { console.warn("localStorage unavailable", e); }
      if (user) {
        try {
          await api.post(`/intakes/${intakeId}/claim`);
          toast.success("Your intake is linked to your portal.");
        } catch (claimErr) {
          console.error("Intake claim failed", claimErr);
          toast.info("Your intake was received. Sign in to your portal and it will link to your account automatically.");
        }
      }
      setStep(7);
    } catch (err) {
      console.error("Intake submit failed", err);
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : err.response
            ? `Server error (${err.response.status}). Please try again — if it persists, call us at (833) 477-7587.`
            : "Could not reach our server. Please check your internet connection and tap Submit again — your answers are still on this page."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const createAccount = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="py-12 lg:py-20" data-testid="start-journey-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="mb-10 text-center">
          <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal mb-3">Start Your Journey</p>
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-navy tracking-tight">Let's organize your journey</h1>
          <p className="mt-3 text-sm text-mutedink">About 10 minutes. Plain questions. Everything confidential.</p>
        </div>

        {/* Progress */}
        <div className="mb-8" data-testid="wizard-progress">
          <div className="flex justify-between mb-2">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className={`flex-1 text-center ${i > 0 ? "" : ""}`}>
                <div className={`h-1.5 rounded-full mx-0.5 ${i <= step ? "bg-teal" : "bg-line"}`} />
              </div>
            ))}
          </div>
          <p className="text-xs text-mutedink text-center">Step {Math.min(step + 1, 8)} of 8 — {STEP_LABELS[step]}</p>
        </div>

        <div className="bg-surface border border-line rounded-2xl p-6 sm:p-8 shadow-sm">
          {step === 0 && (
            <div data-testid="wizard-step-1">
              <h2 className="text-xl font-serif font-semibold text-navy mb-2">What do you need help with?</h2>
              <p className="text-sm text-mutedink mb-6">Choose everything that applies.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {HELP_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => toggleHelp(opt)}
                    data-testid={`help-option-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`rounded-xl border px-4 py-4 text-sm font-medium text-left transition-all ${helpNeeded.includes(opt) ? "border-teal bg-teal-light text-teal" : "border-line bg-ivory text-navy hover:border-teal"}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="wizard-step-2">
              <h2 className="text-xl font-serif font-semibold text-navy mb-2">Are there any urgent deadlines?</h2>
              <p className="text-sm text-mutedink mb-6">This helps us prioritize. Answer yes to anything that applies.</p>
              <div className="space-y-3">
                {URGENT_QUESTIONS.map((q) => (
                  <div key={q.key} className="flex items-center justify-between gap-4 rounded-xl border border-line bg-ivory px-4 py-3">
                    <span className="text-sm text-ink">{q.label}</span>
                    <div className="flex gap-2">
                      {[true, false].map((v) => (
                        <button
                          key={String(v)}
                          onClick={() => setUrgent({ ...urgent, [q.key]: v })}
                          data-testid={`urgent-${q.key}-${v ? "yes" : "no"}`}
                          className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${urgent[q.key] === v ? (v ? "bg-champagne-dark text-white" : "bg-sage text-white") : "bg-surface border border-line text-mutedink"}`}
                        >
                          {v ? "Yes" : "No"}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {urgent.detained && (
                <p className="mt-4 text-sm text-champagne-dark bg-champagne-light border border-champagne/40 rounded-xl p-4">
                  Detained matters are triaged first. A representative will contact you as quickly as possible after you submit.
                </p>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="wizard-step-3">
              <h2 className="text-xl font-serif font-semibold text-navy mb-2">About you</h2>
              <p className="text-sm text-mutedink mb-6">Basic contact information so we can reach you.</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="wj-name">Full name *</Label>
                  <Input id="wj-name" data-testid="wizard-name-input" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="mt-1.5" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="wj-email">Email *</Label>
                    <Input id="wj-email" type="email" data-testid="wizard-email-input" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="wj-phone">Phone *</Label>
                    <Input id="wj-phone" type="tel" data-testid="wizard-phone-input" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="mt-1.5" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="wj-city">City / State where you live</Label>
                    <Input id="wj-city" data-testid="wizard-city-input" value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="wj-country">Country of birth</Label>
                    <Input id="wj-country" data-testid="wizard-country-input" value={contact.country_of_birth} onChange={(e) => setContact({ ...contact, country_of_birth: e.target.value })} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label>Preferred language</Label>
                  <Select value={contact.language} onValueChange={(v) => setContact({ ...contact, language: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="wizard-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["English", "Spanish", "Haitian Creole", "French", "Portuguese", "Arabic", "Mandarin", "Other"].map((l) => (
                        <SelectItem key={l} value={l}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={contact.consent}
                  aria-required="true"
                  aria-label="Consent to communication"
                  onClick={() => setContact({ ...contact, consent: !contact.consent })}
                  data-testid="wizard-consent-check"
                  className={`flex items-start gap-3 rounded-xl border px-4 py-3.5 text-left transition-all w-full ${contact.consent ? "border-teal bg-teal-light" : "border-line bg-ivory hover:border-teal"}`}
                >
                  <span className={`mt-0.5 h-5 w-5 rounded border flex items-center justify-center flex-shrink-0 ${contact.consent ? "bg-teal border-teal" : "border-line bg-white"}`}>
                    {contact.consent && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                  </span>
                  <span className={`text-xs leading-relaxed ${contact.consent ? "text-teal" : "text-ink"}`}>
                    I consent to CF Full Circle contacting me by my chosen methods (portal, email, phone, or text) about my inquiry. Ordinary email and text may carry privacy risks; I can use the secure portal instead. *
                  </span>
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="wizard-step-4">
              <h2 className="text-xl font-serif font-semibold text-navy mb-2">Tell us about your situation</h2>
              <p className="text-sm text-mutedink mb-6">A few sentences is fine. Plain words are perfect.</p>
              <Label htmlFor="wj-situation">What is happening, and what would you like help with?</Label>
              <Textarea id="wj-situation" rows={6} data-testid="wizard-situation-input" value={details.situation} onChange={(e) => setDetails({ ...details, situation: e.target.value })} className="mt-1.5" placeholder="Example: I need to renew my passport and I also want to apply for my husband's green card..." />
            </div>
          )}

          {step === 4 && (
            <div data-testid="wizard-step-5">
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="h-5 w-5 text-teal" />
                <h2 className="text-xl font-serif font-semibold text-navy">Your documents</h2>
              </div>
              <p className="text-sm text-mutedink mb-4 leading-relaxed">
                After you create your secure portal account (next step), you will be able to upload documents safely — directly from your phone camera if you like. For now, just tell us what you already have.
              </p>
              <Label htmlFor="wj-docs">Which documents do you currently have? (passport, birth certificate, visa, etc.)</Label>
              <Textarea id="wj-docs" rows={4} data-testid="wizard-documents-input" value={details.documents_ready} onChange={(e) => setDetails({ ...details, documents_ready: e.target.value })} className="mt-1.5" placeholder="Example: passport (expired), birth certificate, marriage certificate..." />
            </div>
          )}

          {step === 5 && (
            <div data-testid="wizard-step-6">
              <h2 className="text-xl font-serif font-semibold text-navy mb-2">Appointment preference</h2>
              <p className="text-sm text-mutedink mb-6">Tell us what works for you. We will confirm the exact time after reviewing your intake.</p>
              <div className="space-y-4">
                <div>
                  <Label>Appointment type</Label>
                  <Select value={appt.service_type} onValueChange={(v) => setAppt({ ...appt, service_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="wizard-appt-type-select"><SelectValue placeholder="Choose a type" /></SelectTrigger>
                    <SelectContent>
                      {APPOINTMENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>How would you like to meet?</Label>
                  <div className="mt-1.5 grid grid-cols-3 gap-2">
                    {[["In person", "in_person"], ["Video call", "virtual"], ["Phone", "phone"]].map(([label, v]) => (
                      <button
                        key={v}
                        onClick={() => setAppt({ ...appt, mode: v })}
                        data-testid={`wizard-mode-${v}`}
                        className={`rounded-xl border px-3 py-3 text-sm font-medium transition-all ${appt.mode === v ? "border-teal bg-teal-light text-teal" : "border-line bg-ivory text-navy hover:border-teal"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="wj-date">Preferred date</Label>
                    <Input id="wj-date" type="date" data-testid="wizard-appt-date" value={appt.date} onChange={(e) => setAppt({ ...appt, date: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="wj-time">Preferred time</Label>
                    <Input id="wj-time" type="time" data-testid="wizard-appt-time" value={appt.time} onChange={(e) => setAppt({ ...appt, time: e.target.value })} className="mt-1.5" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div data-testid="wizard-step-7">
              <h2 className="text-xl font-serif font-semibold text-navy mb-2">Review &amp; submit</h2>
              <div className="space-y-4 text-sm">
                <div className="rounded-xl bg-ivory border border-line p-4">
                  <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal mb-1">You need help with</p>
                  <p className="text-ink">{helpNeeded.join(", ") || "—"}</p>
                </div>
                <div className="rounded-xl bg-ivory border border-line p-4">
                  <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal mb-1">Urgent matters</p>
                  <p className="text-ink">{hasUrgent ? URGENT_QUESTIONS.filter((q) => urgent[q.key]).map((q) => q.label).join(" ") : "None reported"}</p>
                </div>
                <div className="rounded-xl bg-ivory border border-line p-4">
                  <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal mb-1">Contact</p>
                  <p className="text-ink">{contact.name} · {contact.email} · {contact.phone}</p>
                </div>
              </div>
              <p className="mt-5 text-xs text-mutedink leading-relaxed">
                By submitting, you agree that CF Full Circle may review your information and contact you about your inquiry. Submitting this form does not create a representative-client relationship. See our <a href="/legal" className="text-teal underline">Legal Disclosures</a>.
              </p>
            </div>
          )}

          {step === 7 && (
            <div className="text-center py-6" data-testid="wizard-step-8">
              <div className="h-16 w-16 mx-auto rounded-full bg-sage-light flex items-center justify-center mb-6">
                <PartyPopper className="h-8 w-8 text-sage" />
              </div>
              <h2 className="text-2xl font-serif font-semibold text-navy">Thank you for starting your Full Circle Journey.</h2>
              <p className="mt-4 text-sm text-mutedink leading-relaxed max-w-md mx-auto">
                Your information has been received. {user ? "Sign in to your secure portal to review your next steps, upload requested documents, and receive updates." : "Create your secure portal account to review your next steps, upload requested documents, and receive updates."}
              </p>
              <div className="mt-8 flex flex-col items-center gap-3">
                {user ? (
                  <Button onClick={() => navigate("/portal")} size="lg" className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full px-8" data-testid="wizard-goto-portal-button">
                    Go to My Portal <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button onClick={createAccount} size="lg" className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full px-8" data-testid="wizard-create-account-button">
                    Create My Secure Portal Account <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
                <p className="text-xs text-mutedink flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-sage" /> Secured with Google sign-in — no password to remember.</p>
              </div>
            </div>
          )}

          {step < 7 && (
            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(step - 1)}
                disabled={step === 0}
                className="text-mutedink hover:text-navy"
                data-testid="wizard-back-button"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              {step < 6 ? (
                <Button
                  onClick={() => canNext() ? setStep(step + 1) : toast.error("Please complete this step first.")}
                  className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full px-6"
                  data-testid="wizard-next-button"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full px-6"
                  data-testid="wizard-submit-button"
                >
                  {submitting ? "Submitting..." : "Submit My Journey Request"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
